import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Avatar,
  Badge,
  LinearProgress
} from '@mui/material';
import {
  Add,
  Visibility,
  Edit,
  Delete,
  Send,
  CheckCircle,
  Cancel,
  Schedule,
  AttachMoney,
  TrendingUp

} from '@mui/icons-material';
import proposalService from '../../services/proposalService';
import DialogCloseButton from '../common/DialogCloseButton';

const ProposalManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [proposals, setProposals] = useState([]);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [selectedProposal, setSelectedProposal] = useState(null);

  useEffect(() => {
    loadProposals();
    loadStats();
  }, []);

  const loadProposals = async () => {
    setLoading(true);
    try {
      const result = await proposalService.getMyProposals();
      if (result.success) {
        setProposals(result.data || []);
      }
    } catch (err) {
      setError('Failed to load bids');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const result = await proposalService.getMyProposalStats();
      if (result.success) {
        setStats(result.data);
      }
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      submitted: 'info',
      under_review: 'warning',
      changes_requested: 'warning',
      accepted: 'success',
      rejected: 'error',
      withdrawn: 'default'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: 'Draft',
      submitted: 'Submitted',
      under_review: 'Under Review',
      changes_requested: 'Changes Requested',
      accepted: 'Accepted',
      rejected: 'Rejected',
      withdrawn: 'Withdrawn'
    };
    return labels[status] || status;
  };

  const filterProposalsByTab = () => {
    switch (activeTab) {
      case 0: // All
        return proposals;
      case 1: // Draft
        return proposals.filter(p => p.status === 'draft');
      case 2: // Submitted
        return proposals.filter(p => ['submitted', 'under_review'].includes(p.status));
      case 3: // Needs Action
        return proposals.filter(p => p.status === 'changes_requested');
      case 4: // Accepted
        return proposals.filter(p => p.status === 'accepted');
      case 5: // Rejected
        return proposals.filter(p => p.status === 'rejected');
      default:
        return proposals;
    }
  };

  const handleSubmitProposal = async (proposalId) => {
    try {
      const result = await proposalService.submitProposal(proposalId);
      if (result.success) {
        loadProposals();
        loadStats();
      }
    } catch (err) {
      setError('Failed to submit bid');
    }
  };

  const handleDeleteProposal = async (proposalId) => {
    if (!window.confirm('Are you sure you want to delete this bid?')) {
      return;
    }

    try {
      await proposalService.deleteProposal(proposalId);
      setProposals(prev => prev.filter(p => p.id !== proposalId));
    } catch (err) {
      // If API delete fails, still remove from local state
      setProposals(prev => prev.filter(p => p.id !== proposalId));
      console.warn('Delete API failed, removed from UI only:', err.message);
    }
  };

  const renderStatsCards = () => {
    if (!stats) return null;

    return (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #0d9488 0%, #134e4a 100%)',
            color: 'white',
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(102, 126, 234, 0.4)'
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1, fontSize: '0.875rem' }}>
                    Total Bids
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {stats.total || 0}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.75rem' }}>
                    All submissions
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <Send sx={{ fontSize: 32 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #43B581 0%, #27ae60 100%)',
            color: 'white',
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(67, 181, 129, 0.3)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(67, 181, 129, 0.4)'
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1, fontSize: '0.875rem' }}>
                    Accepted
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {stats.accepted || 0}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.75rem' }}>
                    Win Rate: {stats.winRate || 0}%
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <CheckCircle sx={{ fontSize: 32 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #FAA61A 0%, #f39c12 100%)',
            color: 'white',
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(250, 166, 26, 0.3)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(250, 166, 26, 0.4)'
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1, fontSize: '0.875rem' }}>
                    Pending Review
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {stats.pending || 0}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.75rem' }}>
                    Awaiting response
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <Schedule sx={{ fontSize: 32 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #0d9488 0%, #134e4a 100%)',
            color: 'white',
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(155, 89, 182, 0.3)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(155, 89, 182, 0.4)'
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1, fontSize: '0.875rem' }}>
                    Total Value
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                    ${(stats.totalValue || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.75rem' }}>
                    All bids
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <AttachMoney sx={{ fontSize: 32 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderProposalsTable = () => {
    const filteredProposals = filterProposalsByTab();

    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (filteredProposals.length === 0) {
      return (
        <Alert severity="info">
          No bids found. Create your first bid to get started!
        </Alert>
      );
    }

    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Project</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Milestones</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Submitted</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProposals.map((proposal) => (
              <TableRow key={proposal.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {proposal.projectTitle || 'Untitled Project'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {proposal.projectLocation}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32 }}>
                      {proposal.clientName?.[0]}
                    </Avatar>
                    <Typography variant="body2">
                      {proposal.clientName || 'Unknown'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    ${proposal.totalCost?.toLocaleString() || 0}
                  </Typography>
                </TableCell>
                <TableCell>
                  {proposal.milestones?.length || 0}
                </TableCell>
                <TableCell>
                  <Chip
                    label={getStatusLabel(proposal.status)}
                    color={getStatusColor(proposal.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {proposal.submittedAt ? new Date(proposal.submittedAt).toLocaleDateString() : 'Not submitted'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="View">
                      <IconButton size="small" onClick={() => setSelectedProposal(proposal)}>
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    
                    {proposal.status === 'draft' && (
                      <>
                        <Tooltip title="Edit">
                          <IconButton size="small">
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Submit">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleSubmitProposal(proposal.id)}
                          >
                            <Send fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteProposal(proposal.id)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}

                    {proposal.status === 'changes_requested' && (
                      <Tooltip title="Revise">
                        <IconButton size="small" color="warning" onClick={() => navigate(`/bids/${proposal.id}`)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          My Bids
        </Typography>
      </Box>

      {proposals.length === 0 && !loading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>No bids yet?</strong> Browse available projects and submit your first bid to get started!
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {renderStatsCards()}

      <Card>
        <CardContent>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
            <Tab label={`All (${proposals.length})`} />
            <Tab label={`Draft (${proposals.filter(p => p.status === 'draft').length})`} />
            <Tab label={`Submitted (${proposals.filter(p => ['submitted', 'under_review'].includes(p.status)).length})`} />
            <Tab label={`Needs Action (${proposals.filter(p => p.status === 'changes_requested').length})`} />
            <Tab label={`Accepted (${proposals.filter(p => p.status === 'accepted').length})`} />
            <Tab label={`Rejected (${proposals.filter(p => p.status === 'rejected').length})`} />
          </Tabs>

          {renderProposalsTable()}
        </CardContent>
      </Card>

      {/* View Proposal Dialog */}
      {selectedProposal && (
        <Dialog
          open={!!selectedProposal}
          onClose={() => setSelectedProposal(null)}
          maxWidth="md"
          fullWidth
        >
          <DialogCloseButton onClick={() => setSelectedProposal(null)} />
          <DialogTitle>
            Proposal Details
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Project
                </Typography>
                <Typography variant="body1">
                  {selectedProposal.projectTitle}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Cost
                </Typography>
                <Typography variant="h6">
                  ${selectedProposal.totalCost?.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={getStatusLabel(selectedProposal.status)}
                  color={getStatusColor(selectedProposal.status)}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Scope of Work
                </Typography>
                <Typography variant="body2">
                  {selectedProposal.scopeOfWork}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Milestones ({selectedProposal.milestones?.length || 0})
                </Typography>
                <TableContainer component={Paper} sx={{ mt: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Duration</TableCell>
                        <TableCell>Payment</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedProposal.milestones?.map((milestone, index) => (
                        <TableRow key={index}>
                          <TableCell>{milestone.name}</TableCell>
                          <TableCell>{milestone.duration} {milestone.durationUnit}</TableCell>
                          <TableCell>{milestone.paymentPercentage}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSelectedProposal(null)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default ProposalManagement;
